import { ReloadOutlined } from '@ant-design/icons';
import { history } from '@umijs/max';
import { Alert, Button, Descriptions, Empty, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React from 'react';
import { useEnterpriseDetail } from '@/components/EnterpriseDetailModal';
import PageSection from '@/components/PageSection';
import { enterpriseDetailPath } from '@/constants/routes';
import { useSpaceConfig } from '@/pages/SpaceManagement/spaceConfigStore';
import {
  displayStatusColor,
  statusText,
} from '@/pages/SpaceManagement/spaceLabels';
import {
  deriveSpaceDisplayStatus,
  spaceDisplayStatusText,
} from '@/pages/SpaceManagement/spaceOperation';
import { useSpaceTagOptions } from '@/pages/SpaceManagement/spaceTags';
import { parkApi } from '@/services/bff/park.api';
import type {
  EnterpriseSpaceOccupancy,
  ParkLease,
  ParkSpaceDetail,
  SpaceIncubationBinding,
} from '@/services/bff/types/park';
import { formatDate } from '@/utils/datetime';
import { themeTableColumns } from '@/utils/themeTableColumns';
import styles from './index.module.less';

type DetailSectionKey =
  | 'basic'
  | 'usage'
  | 'incubation'
  | 'lease'
  | 'registration'
  | 'changes';

type NavItem = {
  key: DetailSectionKey;
  label: string;
  count?: number;
};

type EnterpriseLinkInput = {
  name?: string | null;
  tenantUuid?: string | null;
  parkEnterpriseUuid?: string | null;
  /** 兼容 ParkLease / SpaceIncubationBinding / EnterpriseSpaceOccupancy 的原始字段 */
  enterpriseName?: string | null;
  enterpriseTenantUuid?: string | null;
};

const PAGE_SIZE = 10;
const RELATED_PAGE_SIZE = 100;

const leaseStatusText: Record<ParkLease['status'], string> = {
  draft: '草稿',
  active: '生效中',
  expired: '已到期',
  terminated: '已终止',
};

const leaseStatusColor: Record<ParkLease['status'], string> = {
  draft: 'default',
  active: 'success',
  expired: 'warning',
  terminated: 'error',
};

const formatArea = (value?: number | null) => {
  if (value == null || !Number.isFinite(Number(value))) return '-';
  return `${Number(value).toLocaleString('zh-CN', {
    maximumFractionDigits: 2,
  })}㎡`;
};

const formatRent = (lease: ParkLease) => {
  if (lease.rentAmount == null) return '-';
  return `${Number(lease.rentAmount).toLocaleString('zh-CN')} 元 / ${
    lease.rentPeriod || '-'
  }`;
};

const renderPeriod = (start?: string | null, end?: string | null) =>
  `${formatDate(start)} ~ ${end ? formatDate(end) : '长期'}`;

const useIsNarrow = (maxWidth: number) => {
  const [narrow, setNarrow] = React.useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(`(max-width: ${maxWidth}px)`).matches
      : false,
  );
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const update = () => setNarrow(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, [maxWidth]);
  return narrow;
};

const Fact: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className={styles.fact}>
    <span className={styles.factLabel}>{label}</span>
    <span
      className={styles.factValue}
      title={typeof value === 'string' ? value : undefined}
    >
      {value === undefined || value === null || value === '' ? '-' : value}
    </span>
  </div>
);

type SpaceDetailPanelProps = {
  spaceUuid: string;
};

const SpaceDetailPanel: React.FC<SpaceDetailPanelProps> = ({ spaceUuid }) => {
  const { openEnterpriseDetail } = useEnterpriseDetail();
  const uuid = spaceUuid;
  const [detail, setDetail] = React.useState<ParkSpaceDetail>();
  const [detailLoading, setDetailLoading] = React.useState(true);
  const [detailError, setDetailError] = React.useState(false);

  const [bindings, setBindings] = React.useState<SpaceIncubationBinding[]>([]);
  const [bindingTotal, setBindingTotal] = React.useState(0);
  const [bindingsLoading, setBindingsLoading] = React.useState(true);
  const [bindingsError, setBindingsError] = React.useState(false);

  const [leases, setLeases] = React.useState<ParkLease[]>([]);
  const [leaseTotal, setLeaseTotal] = React.useState(0);
  const [leasesLoading, setLeasesLoading] = React.useState(true);
  const [leasesError, setLeasesError] = React.useState(false);

  const [activeSection, setActiveSection] =
    React.useState<DetailSectionKey>('basic');
  const descriptionsColumn = useIsNarrow(600) ? 1 : 2;

  // tagLabelMap 含停用标签，保证已打停用标签的房源仍显示名称而不是裸 value。
  const { tagLabelMap, reloadTags } = useSpaceTagOptions();
  const { labelOf, reload: reloadConfig } = useSpaceConfig();

  const detailSequence = React.useRef(0);
  const bindingSequence = React.useRef(0);
  const leaseSequence = React.useRef(0);
  const contextSequence = React.useRef(0);

  const loadDetail = React.useCallback(
    async (pageNumber = 1) => {
      if (!uuid) return;
      const requestId = ++detailSequence.current;
      const contextId = contextSequence.current;
      setDetailLoading(true);
      setDetailError(false);
      try {
        const spaceDetail = await parkApi.getSpace(uuid, {
          pageNumber,
          pageSize: PAGE_SIZE,
        });
        if (
          requestId !== detailSequence.current ||
          contextId !== contextSequence.current
        )
          return;
        setDetail(spaceDetail);
      } catch {
        if (
          requestId !== detailSequence.current ||
          contextId !== contextSequence.current
        )
          return;
        setDetail(undefined);
        setDetailError(true);
      } finally {
        if (
          requestId === detailSequence.current &&
          contextId === contextSequence.current
        ) {
          setDetailLoading(false);
        }
      }
    },
    [uuid],
  );

  const loadBindings = React.useCallback(async () => {
    if (!uuid) {
      setBindings([]);
      setBindingTotal(0);
      setBindingsError(false);
      setBindingsLoading(false);
      return;
    }
    const requestId = ++bindingSequence.current;
    const contextId = contextSequence.current;
    setBindingsLoading(true);
    setBindingsError(false);
    try {
      const result = await parkApi.listSpaceIncubationBindings({
        spaceUuid: uuid,
        pageNumber: 1,
        pageSize: RELATED_PAGE_SIZE,
      });
      if (
        requestId !== bindingSequence.current ||
        contextId !== contextSequence.current
      )
        return;
      setBindings(result.items || []);
      setBindingTotal(result.total || 0);
    } catch {
      if (
        requestId !== bindingSequence.current ||
        contextId !== contextSequence.current
      )
        return;
      setBindings([]);
      setBindingTotal(0);
      setBindingsError(true);
    } finally {
      if (
        requestId === bindingSequence.current &&
        contextId === contextSequence.current
      ) {
        setBindingsLoading(false);
      }
    }
  }, [uuid]);

  const loadLeases = React.useCallback(async () => {
    if (!uuid) {
      setLeases([]);
      setLeaseTotal(0);
      setLeasesError(false);
      setLeasesLoading(false);
      return;
    }
    const requestId = ++leaseSequence.current;
    const contextId = contextSequence.current;
    setLeasesLoading(true);
    setLeasesError(false);
    try {
      const result = await parkApi.listLeases({
        spaceUuid: uuid,
        pageNumber: 1,
        pageSize: RELATED_PAGE_SIZE,
      });
      if (
        requestId !== leaseSequence.current ||
        contextId !== contextSequence.current
      )
        return;
      setLeases(result.items || []);
      setLeaseTotal(result.total || 0);
    } catch {
      if (
        requestId !== leaseSequence.current ||
        contextId !== contextSequence.current
      )
        return;
      setLeases([]);
      setLeaseTotal(0);
      setLeasesError(true);
    } finally {
      if (
        requestId === leaseSequence.current &&
        contextId === contextSequence.current
      ) {
        setLeasesLoading(false);
      }
    }
  }, [uuid]);

  const resetRelated = React.useCallback(() => {
    setBindings([]);
    setBindingTotal(0);
    setBindingsError(false);
    setLeases([]);
    setLeaseTotal(0);
    setLeasesError(false);
  }, []);

  React.useEffect(() => {
    void loadDetail(1);
    void loadBindings();
    void loadLeases();
    void reloadTags();
    void reloadConfig();

    const handleContextChange = () => {
      contextSequence.current += 1;
      detailSequence.current += 1;
      bindingSequence.current += 1;
      leaseSequence.current += 1;
      setDetail(undefined);
      setDetailError(false);
      resetRelated();
      void loadDetail(1);
      void loadBindings();
      void loadLeases();
      void reloadTags();
      void reloadConfig();
    };

    window.addEventListener('park-context-changed', handleContextChange);
    window.addEventListener('tenant-context-changed', handleContextChange);
    return () => {
      contextSequence.current += 1;
      detailSequence.current += 1;
      bindingSequence.current += 1;
      leaseSequence.current += 1;
      window.removeEventListener('park-context-changed', handleContextChange);
      window.removeEventListener('tenant-context-changed', handleContextChange);
    };
  }, [
    loadBindings,
    loadDetail,
    loadLeases,
    reloadConfig,
    reloadTags,
    resetRelated,
  ]);

  const space = detail?.space;
  const currentOccupancies = detail?.currentOccupancies || [];
  const registrationUnits = detail?.registrationUnits || [];
  const historyOccupancies = detail?.occupancyHistory.items || [];
  const hasRegistration = registrationUnits.length > 0;
  const displayStatus = detail
    ? deriveSpaceDisplayStatus(
        space?.operationMode,
        currentOccupancies.length > 0,
      )
    : null;

  React.useEffect(() => {
    if (activeSection === 'registration' && !hasRegistration) {
      setActiveSection('basic');
    }
  }, [activeSection, hasRegistration]);

  const navItems = React.useMemo<NavItem[]>(() => {
    const items: NavItem[] = [
      { key: 'basic', label: '基本信息' },
      { key: 'usage', label: '使用情况' },
      {
        key: 'incubation',
        label: '注册企业',
        count: bindingsLoading || bindingsError ? undefined : bindingTotal,
      },
      {
        key: 'lease',
        label: '租赁企业',
        count: leasesLoading || leasesError ? undefined : leaseTotal,
      },
    ];
    if (hasRegistration) {
      items.push({
        key: 'registration',
        label: '注册单元',
        count: registrationUnits.length,
      });
    }
    items.push({ key: 'changes', label: '变更记录' });
    return items;
  }, [
    bindingTotal,
    bindingsError,
    bindingsLoading,
    hasRegistration,
    leaseTotal,
    leasesError,
    leasesLoading,
    registrationUnits.length,
  ]);

  const refreshAll = React.useCallback(() => {
    void loadDetail(1);
    void loadBindings();
    void loadLeases();
    void reloadTags();
    void reloadConfig();
  }, [loadBindings, loadDetail, loadLeases, reloadConfig, reloadTags]);

  const renderEnterpriseLink = ({
    name,
    tenantUuid,
    parkEnterpriseUuid,
    enterpriseName,
    enterpriseTenantUuid,
  }: EnterpriseLinkInput) => {
    const resolvedName = enterpriseName ?? name;
    const resolvedTenantUuid = enterpriseTenantUuid ?? tenantUuid;
    const label = resolvedName || resolvedTenantUuid;
    if (!label) return '-';
    if (!resolvedTenantUuid) return label;
    return (
      <Button
        type="link"
        className={styles.enterpriseLink}
        onClick={() =>
          parkEnterpriseUuid
            ? openEnterpriseDetail(parkEnterpriseUuid)
            : history.push(enterpriseDetailPath(resolvedTenantUuid))
        }
      >
        {label}
      </Button>
    );
  };

  const usageColumns: ColumnsType<EnterpriseSpaceOccupancy> = [
    {
      title: '企业',
      key: 'enterpriseName',
      width: 200,
      fixed: 'left',
      render: (_, row) => renderEnterpriseLink(row),
    },
    {
      title: '开始日期',
      dataIndex: 'startDate',
      width: 120,
      render: (value) => formatDate(value),
    },
    {
      title: '预计结束日期',
      dataIndex: 'expectedEndDate',
      width: 130,
      render: (value) => formatDate(value),
    },
    {
      title: '实际结束日期',
      dataIndex: 'endDate',
      width: 130,
      render: (value) => formatDate(value),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (value) =>
        value === 'ended' ? <Tag>已结束</Tag> : <Tag color="success">当前</Tag>,
    },
  ];

  const registrationColumns: ColumnsType<
    NonNullable<ParkSpaceDetail['registrationUnits']>[number]
  > = [
    {
      title: '单元编号',
      key: 'name',
      width: 180,
      fixed: 'left',
      render: (_, row) => row.space.name,
    },
    {
      title: '面积(㎡)',
      key: 'area',
      width: 100,
      render: (_, row) => (row.space.area == null ? '虚拟' : row.space.area),
    },
    {
      title: '派生方式',
      key: 'deriveMode',
      width: 120,
      render: (_, row) =>
        row.space.deriveMode === 'virtual' ? '虚拟房源' : '房间切分',
    },
    {
      title: '挂靠企业',
      key: 'enterprise',
      width: 220,
      render: (_, row) =>
        row.occupancy ? renderEnterpriseLink(row.occupancy) : '-',
    },
    {
      title: '状态',
      key: 'status',
      width: 100,
      render: (_, row) =>
        row.occupancy ? <Tag color="success">已占用</Tag> : <Tag>待分配</Tag>,
    },
  ];

  const incubationColumns: ColumnsType<SpaceIncubationBinding> = [
    {
      title: '企业名称',
      key: 'enterpriseName',
      width: 220,
      fixed: 'left',
      render: (_, row) => renderEnterpriseLink(row),
    },
    {
      title: '统一社会信用代码',
      dataIndex: 'unifiedSocialCreditCode',
      width: 190,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: '起止时间',
      key: 'period',
      width: 220,
      render: (_, row) => renderPeriod(row.startDate, row.endDate),
    },
    {
      title: '注册面积',
      dataIndex: 'registrationArea',
      width: 110,
      render: (value) => (value == null ? '-' : `${value}㎡`),
    },
    {
      title: '备注',
      dataIndex: 'remark',
      width: 180,
      ellipsis: true,
      render: (value) => value || '-',
    },
  ];

  const leaseColumns: ColumnsType<ParkLease> = [
    {
      title: '企业名称',
      key: 'enterpriseName',
      width: 180,
      fixed: 'left',
      render: (_, row) => renderEnterpriseLink(row),
    },
    {
      title: '租赁编号',
      dataIndex: 'leaseNumber',
      width: 130,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: '合同名称',
      dataIndex: 'contractName',
      width: 150,
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: '租期',
      key: 'period',
      width: 190,
      render: (_, row) => renderPeriod(row.startDate, row.endDate),
    },
    {
      title: '租赁面积',
      dataIndex: 'leaseArea',
      width: 90,
      render: (value) => formatArea(value),
    },
    {
      title: '租金',
      key: 'rent',
      width: 120,
      render: (_, row) => formatRent(row),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 80,
      render: (value: ParkLease['status']) => (
        <Tag color={leaseStatusColor[value]}>
          {leaseStatusText[value] || value}
        </Tag>
      ),
    },
  ];

  const renderBasicSection = () => (
    <PageSection title="基本信息" loading={detailLoading && !detail}>
      {detail ? (
        <Descriptions column={descriptionsColumn} size="small">
          <Descriptions.Item label="空间名称">
            {detail.space.name}
          </Descriptions.Item>
          <Descriptions.Item label="房源类型">
            {labelOf('space_type', detail.space.spaceType)}
          </Descriptions.Item>
          <Descriptions.Item label="父空间">
            {detail.parentName || '顶级空间'}
          </Descriptions.Item>
          <Descriptions.Item label="面积(㎡)">
            {detail.space.area ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="所属路径" span={2}>
            {detail.path || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="经营属性">
            {labelOf('operation_mode', detail.space.operationMode)}
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            {statusText[detail.space.status]}
          </Descriptions.Item>
          <Descriptions.Item label="房源标签" span={2}>
            {detail.space.tags?.length ? (
              <Space size={4} wrap>
                {detail.space.tags.map((tag) => (
                  <Tag key={tag}>{tagLabelMap.get(tag) || tag}</Tag>
                ))}
              </Space>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="备注" span={2}>
            {detail.space.remark || '-'}
          </Descriptions.Item>
        </Descriptions>
      ) : (
        <Empty
          description={detailError ? '房源详情加载失败' : '暂无空间信息'}
        />
      )}
    </PageSection>
  );

  const renderUsageSection = () => (
    <PageSection title="使用情况">
      {detail ? (
        <div className={styles.stack}>
          <Descriptions column={descriptionsColumn} size="small">
            <Descriptions.Item label="当前占用状态">
              {displayStatus ? (
                <Tag color={displayStatusColor[displayStatus]}>
                  {spaceDisplayStatusText[displayStatus]}
                </Tag>
              ) : (
                '-'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="当前使用企业">
              {currentOccupancies.length
                ? currentOccupancies.map((item) => (
                    <span key={item.uuid} className={styles.enterpriseInline}>
                      {renderEnterpriseLink(item)}
                    </span>
                  ))
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="使用开始时间">
              {formatDate(currentOccupancies[0]?.startDate)}
            </Descriptions.Item>
            <Descriptions.Item label="预计结束时间">
              {formatDate(currentOccupancies[0]?.expectedEndDate)}
            </Descriptions.Item>
          </Descriptions>

          <div>
            <div className={styles.subTitle}>当前占用记录</div>
            <div data-testid="current-occupancy-table">
              <Table<EnterpriseSpaceOccupancy>
                rowKey="uuid"
                size="small"
                pagination={false}
                dataSource={currentOccupancies}
                locale={{ emptyText: '当前空置' }}
                columns={themeTableColumns(
                  usageColumns.filter(
                    (column) => column.title !== '实际结束日期',
                  ),
                )}
                scroll={{ x: 560 }}
              />
            </div>
          </div>

          <div>
            <div className={styles.subTitle}>历史占用记录</div>
            <div data-testid="occupancy-history-table">
              <Table<EnterpriseSpaceOccupancy>
                rowKey="uuid"
                size="small"
                dataSource={historyOccupancies}
                locale={{ emptyText: '暂无历史占用' }}
                columns={themeTableColumns(usageColumns)}
                scroll={{ x: 780 }}
                pagination={{
                  current: detail.occupancyHistory.pageNumber,
                  pageSize: detail.occupancyHistory.pageSize,
                  total: detail.occupancyHistory.total,
                  showSizeChanger: false,
                  onChange: (page) => void loadDetail(page),
                }}
              />
            </div>
          </div>
        </div>
      ) : (
        <Empty description="暂无使用情况" />
      )}
    </PageSection>
  );

  const renderIncubationSection = () => (
    <PageSection
      title="注册企业"
      extra={
        <span className={styles.sectionMeta}>
          {bindingsError
            ? '-'
            : bindingTotal > bindings.length
              ? `共 ${bindingTotal} 家，展示前 ${bindings.length} 家`
              : `共 ${bindingTotal} 家`}
        </span>
      }
    >
      {bindingsError ? (
        <Alert
          type="error"
          showIcon
          title="注册企业加载失败"
          action={
            <Button size="small" onClick={() => void loadBindings()}>
              重试
            </Button>
          }
        />
      ) : (
        <Table<SpaceIncubationBinding>
          rowKey="uuid"
          size="small"
          tableLayout="fixed"
          loading={bindingsLoading}
          pagination={false}
          dataSource={bindings}
          locale={{ emptyText: '该房间暂无注册企业' }}
          columns={themeTableColumns(incubationColumns)}
          scroll={{ x: 900 }}
        />
      )}
    </PageSection>
  );

  const renderLeaseSection = () => (
    <PageSection
      title="租赁企业"
      extra={
        <span className={styles.sectionMeta}>
          {leasesError
            ? '-'
            : leaseTotal > leases.length
              ? `共 ${leaseTotal} 条，展示前 ${leases.length} 条`
              : `共 ${leaseTotal} 条`}
        </span>
      }
    >
      {leasesError ? (
        <Alert
          type="error"
          showIcon
          title="租赁企业加载失败"
          action={
            <Button size="small" onClick={() => void loadLeases()}>
              重试
            </Button>
          }
        />
      ) : (
        <div className={styles.tableWrap}>
          <Table<ParkLease>
            rowKey="uuid"
            size="small"
            tableLayout="fixed"
            loading={leasesLoading}
            pagination={false}
            dataSource={leases}
            locale={{ emptyText: '该房间暂无租赁记录' }}
            columns={themeTableColumns(leaseColumns)}
            scroll={{ x: 940 }}
          />
        </div>
      )}
    </PageSection>
  );

  const renderRegistrationSection = () => (
    <PageSection
      title="注册单元"
      extra={
        <span className={styles.sectionMeta}>
          共 {registrationUnits.length} 个
        </span>
      }
    >
      <Table
        rowKey={(row) => row.space.uuid}
        size="small"
        pagination={false}
        dataSource={registrationUnits}
        locale={{ emptyText: '暂无注册单元' }}
        columns={themeTableColumns(registrationColumns)}
        scroll={{ x: 760 }}
      />
    </PageSection>
  );

  const renderChangesSection = () => (
    <PageSection title="变更记录">
      <div data-testid="space-change-log">
        <Empty description="当前暂无可用的空间变更记录" />
      </div>
    </PageSection>
  );

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'usage':
        return renderUsageSection();
      case 'incubation':
        return renderIncubationSection();
      case 'lease':
        return renderLeaseSection();
      case 'registration':
        return renderRegistrationSection();
      case 'changes':
        return renderChangesSection();
      default:
        return renderBasicSection();
    }
  };

  const currentStatus = displayStatus ? (
    <Tag color={displayStatusColor[displayStatus]}>
      {spaceDisplayStatusText[displayStatus]}
    </Tag>
  ) : (
    '-'
  );

  const bindingFact = bindingsLoading
    ? '加载中'
    : bindingsError
      ? '-'
      : `${bindingTotal} 家`;
  const leaseFact = leasesLoading
    ? '加载中'
    : leasesError
      ? '-'
      : `${leaseTotal} 条`;

  return (
    <div className={styles.root}>
      <div className={styles.summary}>
        <div className={styles.summaryMain}>
          <div className={styles.titleRow}>
            <h2 className={styles.title}>{detail?.space.name || '房间详情'}</h2>
            <Button
              icon={<ReloadOutlined />}
              loading={detailLoading || bindingsLoading || leasesLoading}
              onClick={refreshAll}
            >
              刷新
            </Button>
          </div>

          <Space size={[8, 8]} wrap>
            {space ? (
              <Tag color="blue">{labelOf('space_type', space.spaceType)}</Tag>
            ) : null}
            {space?.operationMode ? (
              <Tag>{labelOf('operation_mode', space.operationMode)}</Tag>
            ) : null}
            {space ? (
              <Tag color={space.status === 'active' ? 'success' : 'default'}>
                {statusText[space.status]}
              </Tag>
            ) : null}
            {space?.deriveMode === 'virtual' ? (
              <Tag color="cyan">虚拟房源</Tag>
            ) : null}
          </Space>

          <div className={styles.path} title={detail?.path || undefined}>
            {detail?.path || '—'}
          </div>

          <div className={styles.facts}>
            <Fact
              label="面积(㎡)"
              value={space?.area == null ? '-' : formatArea(space.area)}
            />
            <Fact label="当前使用状态" value={detail ? currentStatus : '-'} />
            <Fact label="注册企业" value={bindingFact} />
            <Fact label="租赁企业" value={leaseFact} />
          </div>
        </div>
      </div>

      <div className={styles.layout}>
        <nav className={styles.nav} aria-label="房源详情分区">
          {navItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={[
                styles.navButton,
                activeSection === item.key ? styles.navButtonActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-current={activeSection === item.key ? 'page' : undefined}
              data-section={item.key}
              onClick={() => setActiveSection(item.key)}
            >
              <span>{item.label}</span>
              {typeof item.count === 'number' ? (
                <span className={styles.navCount}>{item.count}</span>
              ) : null}
            </button>
          ))}
        </nav>

        <div className={styles.content}>
          {detailError && !detail ? (
            <Alert
              type="error"
              showIcon
              title="房源详情加载失败"
              action={
                <Button size="small" onClick={() => void loadDetail(1)}>
                  重试
                </Button>
              }
            />
          ) : null}
          {renderActiveSection()}
        </div>
      </div>
    </div>
  );
};

export default SpaceDetailPanel;
